import React from 'react';
import { Filter, PlusCircle, Search } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog';
import type { FeedbackReportMetadata } from './types';

type FeedbackReportsFiltersProps = {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilters: string[];
  severityFilters: string[];
  metadata: FeedbackReportMetadata;
  onToggleStatus: (value: string) => void;
  onToggleSeverity: (value: string) => void;
  onClearFilters: () => void;
  appliedSearch: string;
  createDialogOpen: boolean;
  onToggleCreateDialog: (open: boolean) => void;
  createFeedbackText: string;
  onChangeCreateFeedbackText: (value: string) => void;
  createError: string | null;
  createStatus: string;
  onChangeCreateStatus: (value: string) => void;
  createRequiresSeverity: boolean;
  createSeverity: string;
  onChangeCreateSeverity: (value: string) => void;
  createSaving: boolean;
  isCreateDisabled: boolean;
  onSubmitCreate: () => void;
};

export const FeedbackReportsFilters: React.FC<FeedbackReportsFiltersProps> = ({
  search,
  onSearchChange,
  statusFilters,
  severityFilters,
  metadata,
  onToggleStatus,
  onToggleSeverity,
  onClearFilters,
  appliedSearch,
  createDialogOpen,
  onToggleCreateDialog,
  createFeedbackText,
  onChangeCreateFeedbackText,
  createError,
  createStatus,
  onChangeCreateStatus,
  createRequiresSeverity,
  createSeverity,
  onChangeCreateSeverity,
  createSaving,
  isCreateDisabled,
  onSubmitCreate,
}) => {
  return (
    <div className="space-y-4 rounded-3xl border border-border/40 bg-white/60 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">New report</h2>
          <p className="text-xs text-muted-foreground">Log a new bug report manually.</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={onToggleCreateDialog}>
          <DialogTrigger asChild>
            <Button size="auto">
              <PlusCircle className="mr-2 h-4 w-4" />
              Create report
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Create feedback report</DialogTitle>
            </DialogHeader>
            {createError ? (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {createError}
              </div>
            ) : null}
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Feedback details
                </label>
                <Textarea
                  value={createFeedbackText}
                  onChange={(event) => onChangeCreateFeedbackText(event.target.value)}
                  rows={4}
                  placeholder="Capture the feedback exactly as reported..."
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Status
                </label>
                <Select value={createStatus} onValueChange={onChangeCreateStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Array.isArray(metadata.status_choices) ? metadata.status_choices : []).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {createRequiresSeverity ? (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Severity
                  </label>
                  <Select value={createSeverity || undefined} onValueChange={onChangeCreateSeverity}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a severity" />
                    </SelectTrigger>
                    <SelectContent>
                      {(Array.isArray(metadata.severity_choices) ? metadata.severity_choices : []).map(
                        (option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                size="auto"
                onClick={() => onToggleCreateDialog(false)}
                disabled={createSaving}
              >
                Cancel
              </Button>
              <Button type="button" size="auto" onClick={onSubmitCreate} disabled={isCreateDisabled}>
                {createSaving ? 'Creating...' : 'Create report'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Search feedback
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search feedback text..."
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Status
        </label>
        <div className="flex flex-col items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                {statusFilters.length > 0 ? `Status (${statusFilters.length})` : 'All statuses'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {(metadata.status_choices ?? []).length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">No statuses available.</div>
              ) : (
                (metadata.status_choices ?? []).map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={statusFilters.includes(option.value)}
                    onCheckedChange={() => onToggleStatus(option.value)}
                  >
                    {option.label}
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                {severityFilters.length > 0 ? `Severity (${severityFilters.length})` : 'All severities'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {(metadata.severity_choices ?? []).length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">No severities available.</div>
              ) : (
                (metadata.severity_choices ?? []).map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={severityFilters.includes(option.value)}
                    onCheckedChange={() => onToggleSeverity(option.value)}
                  >
                    {option.label}
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            type="button"
            onClick={onClearFilters}
            disabled={statusFilters.length === 0 && severityFilters.length === 0 && !appliedSearch}
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear filters
          </button>
        </div>
      </div>
    </div>
  );
};
